import React, { useState } from 'react';
import type { CallList } from 'bundles/connect/types';
import { Form } from 'react-bootstrap-5';

import { Button } from 'components/ui/button';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import { updateCallList, createCallList } from 'bundles/connect/helpers/prospect_api';
import ManageCallListUsers, { type CallListUser } from './manage_call_list_users';

type CallListDetailsProps = {
  callList?: CallList;
  callListUsers: CallListUser[];
  callListName: string;
  setCallListName: React.Dispatch<React.SetStateAction<string>>;
  onSubmit?: () => void;
}

export default function CallListDetails(
  { callList, callListUsers, callListName, setCallListName, onSubmit }: CallListDetailsProps
) {
  const [name, setName] = useState(callListName);
  const [description, setDescription] = useState(callList?.description || '');
  const [checkedUsers, setCheckedUsers] = useState(
    callListUsers.map(user => user.onList || user.currentUser ? user.id : -1).filter(id => id !== -1)
  );
  const [saving, setSaving] = useState(false);
  const [addMembers, setAddMembers] = useState(checkedUsers.length > 1);

  const disabled = saving || (name?.length ?? 0) < 1;

  const handleAddMemberToggle = () => {
    setAddMembers(!addMembers);
    if (addMembers) {
      setCheckedUsers(callListUsers.filter(user => user.currentUser).map(user => user.id));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);

    if (callList) {
      updateCallList(callList.id, { name, description, call_list_members: checkedUsers })
        .then((res) => {
          displayToast('Call list updated successfully', 'success');
          setCallListName(res.name);
        }).finally(() => {
          setSaving(false);
        });
    } else {
      createCallList(name, description, checkedUsers)
        .then((newCallList: CallList) => {
          window.location.href = `/prospect/connect_call_lists/${newCallList.id}`;
        })
        .finally(() => setSaving(false));
    }

    if (onSubmit) {
      onSubmit();
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-5">
        <Form.Label htmlFor="call-list-name-input">Name <small className="text-danger">(required)</small></Form.Label>
        <Form.Control
          id="call-list-name-input"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </Form.Group>
      <Form.Group className="mb-5">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />
      </Form.Group>
      {callListUsers.length > 1 &&
        <>
          <Form.Group className="mb-3" controlId="formShareWithOrg">
            <Form.Check
              checked={addMembers}
              className="d-flex gap-1"
              label={
                <div className="d-flex flex-column">
                  Share with Organization
                  <small className="text-muted mt-2">
                    {callListUsers.length} Team Members
                  </small>
                </div>
              }
              type="switch"
              onChange={handleAddMemberToggle}
            />
          </Form.Group>

          {addMembers && (
            <ManageCallListUsers
              checkedUsers={checkedUsers}
              setCheckedUsers={setCheckedUsers}
              users={callListUsers}
            />
          )}
        </>
      }

      <Button disabled={disabled} type="submit">
        {callList ? 'Update' : 'Create'} List
      </Button>
    </Form>
  );
}
